import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import {
  ISSUE_STATUS_LABEL, ISSUE_STATUS_PILL,
  MAGENTA, SEVERITY_BAR, SEVERITY_LABEL, formatRelative,
} from './maintenance-shared.js';
import { NewIssueModal } from './NewIssueModal.jsx';
import { TicketNumberEditor } from './TicketNumberEditor.jsx';

// Equipment has no detail route (server-only module) — only link when this
// is a printer; render equipment (or a deleted machine) as plain text.
const machineHref = (issue) => (issue.printerId ? `/printers/${issue.printerId}` : null);

export const IssueDetailPage = ({ id }) => {
  const { route } = useLocation();
  const { data: issue, loading, error, refetch } = useApi(`/maintenance/issues/${id}`);
  const eventsPath = !issue ? null
    : issue.printerId ? `/maintenance/events?printerId=${issue.printerId}`
    : issue.equipmentId ? `/maintenance/events?equipmentId=${issue.equipmentId}`
    : null;
  const { data: events } = useApi(eventsPath);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const patchStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/maintenance/issues/${id}`, { status });
      refetch();
    } catch (err) {
      alert(err.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this issue? This can\'t be undone.')) return;
    try {
      await api.delete(`/maintenance/issues/${id}`);
      route('/maintenance');
    } catch (err) {
      alert(err.message ?? 'Failed');
    }
  };

  if (loading) return <p class="text-sm text-text-tertiary">Loading…</p>;
  if (error) return <p class="text-sm font-semibold text-ink-magenta">Failed to load: {error.message}</p>;
  if (!issue) return null;

  const relatedEvents = (events ?? []).filter((e) => e.relatedIssueId === issue.id);

  return (
    <div>
      <a href="/maintenance" class="mb-3 inline-block text-[12px] font-semibold uppercase tracking-eyebrow text-text-tertiary hover:text-text-primary">
        ← Issues
      </a>

      <section class="mb-6 flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: MAGENTA }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: MAGENTA }}>
              Issue <TicketNumberEditor row={issue} resource="issues" isAdmin={isAdmin} onSaved={refetch} />
              {issue.categoryName ? ` · ${issue.categoryName}` : ''}
            </span>
          </div>
          <h1 class="m-0 mb-2 text-[28px] leading-[1.18] tracking-tight text-text-primary">{issue.title}</h1>
          <div class="flex flex-wrap items-center gap-2">
            {issue.affectsProduction && (
              <span class="rounded-full border border-ink-magenta/[0.3] bg-ink-magenta/[0.12] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.04em] text-ink-magenta">
                Affects production
              </span>
            )}
            <span class={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${ISSUE_STATUS_PILL[issue.status]}`}>
              {ISSUE_STATUS_LABEL[issue.status]}
            </span>
            {machineHref(issue) ? (
              <a href={machineHref(issue)}
                class="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-bg-base px-2.5 py-0.5 text-[11px] font-semibold text-text-tertiary hover:text-text-body">
                <span class="block h-1.5 w-1.5 rounded-full bg-ink-teal" />
                {issue.machineName ?? '—'}
              </a>
            ) : (
              <span class="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-bg-base px-2.5 py-0.5 text-[11px] font-semibold text-text-tertiary">
                <span class="block h-1.5 w-1.5 rounded-full bg-ink-teal" />
                {issue.machineName ?? '—'}
              </span>
            )}
          </div>
        </div>
        <div class="flex shrink-0 gap-2">
          {issue.status !== 'resolved' && issue.status !== 'closed' && (
            <Button variant="primary" onClick={() => patchStatus('resolved')} disabled={busy}>Mark resolved</Button>
          )}
          {issue.status === 'resolved' && (
            <Button variant="secondary" onClick={() => patchStatus('closed')} disabled={busy}>Close</Button>
          )}
          <Button variant="secondary" onClick={() => setEditOpen(true)}>Edit</Button>
          {isAdmin && (
            <Button variant="ghost" onClick={remove} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          )}
        </div>
      </section>

      <NewIssueModal
        open={editOpen}
        issue={issue}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); refetch(); }}
      />

      <div class="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Reported</p>
          <table class="w-full text-[13px]">
            <tbody>
              <tr><td class="w-2/5 py-1 text-text-tertiary">By</td><td class="py-1 text-text-body">{issue.reporterName ?? '—'}</td></tr>
              <tr><td class="py-1 text-text-tertiary">When</td><td class="py-1 text-text-body">{formatRelative(issue.createdAt)}</td></tr>
              <tr><td class="py-1 text-text-tertiary">Severity</td>
                  <td class="py-1 text-text-body">
                    <span class="inline-flex items-center gap-2">
                      <span class={`block h-2 w-2 rounded-full ${SEVERITY_BAR[issue.severity]?.replace('bg-', 'bg-')}`} />
                      {SEVERITY_LABEL[issue.severity]}
                    </span>
                  </td></tr>
              <tr><td class="py-1 text-text-tertiary">Category</td><td class="py-1 capitalize text-text-body">{issue.categoryName ?? '—'}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Assigned</p>
          <table class="w-full text-[13px]">
            <tbody>
              <tr><td class="w-2/5 py-1 text-text-tertiary">Internal</td><td class="py-1 text-text-body">{issue.assigneeName ?? <span class="text-text-muted">—</span>}</td></tr>
              <tr><td class="py-1 text-text-tertiary">External</td><td class="py-1 text-text-body">{issue.assigneeContact ?? <span class="text-text-muted">—</span>}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {issue.details && (
        <div class="mb-6 rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Details</p>
          <p class="m-0 whitespace-pre-wrap text-[13px] font-medium leading-relaxed text-text-body">{issue.details}</p>
        </div>
      )}

      <h2 class="mb-3 font-serif text-[18px] font-medium text-text-primary">Related maintenance</h2>
      {relatedEvents.length === 0 ? (
        <p class="mb-6 text-sm text-text-tertiary">No related events yet. Log a maintenance event and link it to this issue.</p>
      ) : (
        <div class="mb-6 overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
          {relatedEvents.map((e) => (
            <a key={e.id} href={`/maintenance/events/${e.id}`}
              class="flex items-center justify-between gap-3 border-b border-border-subtle px-4 py-2 last:border-b-0 hover:bg-bg-hover">
              <div>
                <p class="m-0 text-[13px] font-semibold text-text-primary">{e.title}</p>
                <p class="m-0 text-[11px] text-text-tertiary">{e.kind} · {e.status} · {formatRelative(e.performedAt ?? e.scheduledAt ?? e.createdAt)}</p>
              </div>
            </a>
          ))}
        </div>
      )}

      <div class="rounded-lg border border-dashed border-border-strong bg-bg-card p-4">
        <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-brand">Coming with v2</p>
        <p class="m-0 mt-1 text-[13px] text-text-secondary">Full activity log with comments, attachments, status history, and notification settings — parked in V2-BACKLOG.md.</p>
      </div>
    </div>
  );
};
